import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { CADENA_VACIA, tiempoRecargaEstilosCaptchaV3, tiempoRecargaV2, UMBRAL_ANCHO_VENTANA } from 'utils/constants';

/**
 * Palabras clave de errores de reCAPTCHA en promesas rechazadas.
 */
const PALABRAS_ERROR_RECAPTCHA = ['grecaptcha', 'recaptcha', 'Invalid site key', '[G6()]', '[XS]'];

/**
 * Tiempo máximo de espera para que cargue grecaptcha antes de fallar a v2.
 */
const TIEMPO_ESPERA_CARGA_V3_MS = 5000;

/**
 * Captcha invisible v3.
 * El callback hacia el padre se estabiliza con ref para evitar que GoogleReCaptcha
 * re-ejecute execute() en bucle cuando el padre re-renderiza.
 * @param onVerify callback con el token generado
 * @param onError callback cuando v3 no carga o falla (para mostrar v2)
 */
const CaptchaV3 = ({ onVerify, onError }: { onVerify: (token: string) => void; onError?: () => void }) => {
  /**
   * Clave de Google reCAPTCHA desde el entorno de variables
   */
  const claveGoogleCaptchaV3 = process.env.REACT_APP_CAPTCHA_KEY_V3 || CADENA_VACIA;

  /**
   * Estado para rastrear el ancho actual de la ventana
   */
  const [anchoVentana, setAnchoVentana] = useState(window.innerWidth);

  /**
   * Alterna refreshReCaptcha para renovar el token sin remontar el Provider.
   */
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  /**
   * Evita notificar onError más de una vez.
   */
  const errorNotificadoRef = useRef(false);

  /**
   * Obtiene el valor del estado del captcha v3
   */
  const estadoCaptchaV3 = useSelector((state: RootState) => state.desconexion.captchaV3Estado);

  /**
   * Última versión de onVerify / onError sin cambiar identidades hacia Google.
   */
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  /**
   * Notifica fallo a v2 una sola vez.
   */
  const notificarError = useCallback(() => {
    if (errorNotificadoRef.current) {
      return;
    }
    errorNotificadoRef.current = true;
    const notificar = onErrorRef.current;
    if (notificar) {
      notificar();
    }
  }, []);

  /**
   * Callback estable: GoogleReCaptcha solo re-ejecuta si cambia esta referencia.
   * @param token token emitido por Google
   */
  const stableOnVerify = useCallback(
    (token: string) => {
      if (!token) {
        notificarError();
        return;
      }
      onVerifyRef.current(token);
    },
    [notificarError]
  );

  /**
   * UseEffect manejador de cambio de tamaño de pantalla
   */
  useEffect(() => {
    const manejarCambioTamaño = () => {
      const nuevoAncho = window.innerWidth;
      if (
        (anchoVentana <= UMBRAL_ANCHO_VENTANA && nuevoAncho > UMBRAL_ANCHO_VENTANA) ||
        (anchoVentana > UMBRAL_ANCHO_VENTANA && nuevoAncho <= UMBRAL_ANCHO_VENTANA)
      ) {
        window.location.reload();
      }
      setAnchoVentana(nuevoAncho);
    };

    window.addEventListener('resize', manejarCambioTamaño);
    return () => {
      window.removeEventListener('resize', manejarCambioTamaño);
    };
  }, [anchoVentana]);

  /**
   * UseEffect para esconder el captcha v3
   */
  useEffect(() => {
    let cancelado = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    /**
     * Manejador de estilo del elemento grecaptcha-badge basado en estadoCaptchaV3
     */
    const modificarEstiloBadge = () => {
      if (cancelado) {
        return;
      }
      const badgeElement = document.querySelector('.grecaptcha-badge') as HTMLElement;

      if (badgeElement) {
        if (!estadoCaptchaV3) {
          badgeElement.style.height = '0';
          badgeElement.style.overflow = 'hidden';
        } else {
          badgeElement.style.height = '';
          badgeElement.style.overflow = '';
        }
      } else {
        timeoutId = setTimeout(modificarEstiloBadge, tiempoRecargaEstilosCaptchaV3);
      }
    };

    modificarEstiloBadge();
    return () => {
      cancelado = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [estadoCaptchaV3]);

  /**
   * Si grecaptcha no carga a tiempo, se fuerza el fallback a v2.
   */
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof window !== 'undefined' && !(window as any).grecaptcha) {
        notificarError();
      }
    }, TIEMPO_ESPERA_CARGA_V3_MS);
    return () => window.clearTimeout(timeoutId);
  }, [notificarError]);

  /**
   * Captura rechazos de promesa de Google (p. ej. Invalid site key) para activar v2.
   */
  useEffect(() => {
    /**
     * Detecta errores de reCAPTCHA no capturados.
     * @param event rechazo de promesa
     */
    const handleRejection = (event: PromiseRejectionEvent) => {
      const msg = String(event?.reason?.message || event?.reason || CADENA_VACIA);
      if (PALABRAS_ERROR_RECAPTCHA.some(palabra => msg.includes(palabra))) {
        event.preventDefault();
        notificarError();
      }
    };
    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, [notificarError]);

  /**
   * Renueva el token automáticamente cada 2 minutos sin remontar el Provider.
   */
  useEffect(() => {
    const intervalo = setInterval(() => {
      setRefreshReCaptcha(valorPrevio => !valorPrevio);
    }, tiempoRecargaV2);

    return () => clearInterval(intervalo);
  }, []);

  /**
   * Al desmontar, limpia scripts/badge de v3 para no interferir con el checkbox v2.
   */
  useEffect(
    () => () => {
      document.querySelectorAll('script[src*="recaptcha"]').forEach(script => script.remove());
      document.querySelectorAll('.grecaptcha-badge').forEach(badge => badge.remove());
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).grecaptcha;
    },
    []
  );

  if (!claveGoogleCaptchaV3) {
    return null;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={claveGoogleCaptchaV3} data-testid="recaptcha-v3">
      <GoogleReCaptcha onVerify={stableOnVerify} refreshReCaptcha={refreshReCaptcha} />
    </GoogleReCaptchaProvider>
  );
};

CaptchaV3.defaultProps = {
  onError: undefined,
};

export default CaptchaV3;
